import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab,
  Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingIcon, People as PeopleIcon,
  VideoLibrary as VideoIcon, Add as AddIcon, Dashboard as DashboardIcon,
  Warning as WarningIcon, Close as CloseIcon, PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import axios from 'axios';
import ReleaseComplianceWidget from '../components/ReleaseComplianceWidget';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

function Dashboard() {
  const navigate = useNavigate();
  const [recentContent, setRecentContent] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0); // 0=Weekly, 1=Monthly
  const [selectedWeek, setSelectedWeek] = useState(0); // 0=current, 1=last week, etc
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  // Person work popup
  const [personPopup, setPersonPopup] = useState(null); // { name, role, items, loading }

  const openPersonWork = async (name, personId, role) => {
    const isWeekly = selectedTab === 0;
    // Build period label
    let periodLabel = '';
    if (isWeekly) {
      const getWeekStart = (off) => { const d = new Date(); d.setDate(d.getDate() - off * 7); const day = d.getDay(); d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); return d; };
      const ws = getWeekStart(selectedWeek);
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      periodLabel = `Week: ${ws.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} – ${we.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    } else {
      const [y, m] = selectedMonth.split('-');
      periodLabel = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    setPersonPopup({ name, role, items: [], loading: true, periodLabel });
    try {
      const params = { role, personId };
      if (isWeekly) { params.weekOffset = selectedWeek; }
      else { const [y, m] = selectedMonth.split('-'); params.year = y; params.month = m; }
      const res = await axios.get(`${API_URL}/content/person-work`, { params });
      setPersonPopup({ name, role, items: res.data.data.content || [], loading: false, periodLabel });
    } catch (e) {
      setPersonPopup({ name, role, items: [], loading: false, periodLabel });
    }
  };

  const exportPersonWorkPDF = (popup) => {
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    const items = popup.items || [];
    // Exclude "Content Delivered" from performance totals
    const perfItems     = items.filter(c => !(c.roles || []).every(r => r === 'Content Delivered'));
    const deliveredItems = items.filter(c => (c.roles || []).includes('Content Delivered') && !(c.roles || []).some(r => r !== 'Content Delivered'));
    const longCount  = perfItems.filter(c => c.contentType === 'Long Video').length;
    const shortCount = perfItems.filter(c => c.contentType === 'Shorts').length;
    const adCount    = perfItems.filter(c => c.contentType !== 'Long Video' && c.contentType !== 'Shorts').length;
    const roles = [...new Set(items.flatMap(c => c.roles || []))].join(', ') || 'N/A';
    const makeRow = c => `
      <tr>
        <td>${c.contentId || ''}</td>
        <td>${c.contentTitle || ''}</td>
        <td>${c.contentType || ''}</td>
        <td>${(c.roles || []).join(', ')}</td>
        <td>${c.status || ''}</td>
        <td>${fmt(c.contentReadyAt || c.createdAt)}</td>
        <td>${fmt(c.shootCompletedAt)}</td>
        <td>${fmt(c.editingCompletedAt)}</td>
        <td>${fmt(c.releasedAt)}</td>
      </tr>`;
    const perfRows      = perfItems.map(makeRow).join('');
    const deliveredRows = deliveredItems.map(makeRow).join('');
    const longItems  = perfItems.filter(c => c.contentType === 'Long Video');
    const shortItems = perfItems.filter(c => c.contentType === 'Shorts');
    const adItems    = perfItems.filter(c => c.contentType !== 'Long Video' && c.contentType !== 'Shorts');

    const perfTableHeader = `<thead><tr>
      <th>Content ID</th><th>Title</th><th>Role(s)</th><th>Status</th><th>Content Ready</th><th>Shoot Completed</th><th>Editing Completed</th><th>Release Date</th><th>Edit Hrs</th><th>Dir Hrs</th>
    </tr></thead>`;
    const makeRows = list => list.map(c => `
      <tr>
        <td>${c.contentId||''}</td><td>${c.contentTitle||''}</td>
        <td>${(c.roles||[]).join(', ')}</td><td>${c.status||''}</td>
        <td>${fmt(c.contentReadyAt||c.createdAt)}</td><td>${fmt(c.shootCompletedAt)}</td>
        <td>${fmt(c.editingCompletedAt)}</td><td>${fmt(c.releasedAt)}</td>
        <td>${c.totalEditingEffort != null ? c.totalEditingEffort + ' hrs' : '—'}</td>
        <td>${c.totalDirectingEffort != null ? c.totalDirectingEffort + ' hrs' : '—'}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Performance — ${popup.name}</title>
<style>
  body{font-family:Arial,sans-serif;margin:28px;font-size:12px;color:#111;}
  .header{text-align:right;border-bottom:2px solid #1a237e;padding-bottom:10px;margin-bottom:14px;}
  .header h2{font-size:20px;margin:0 0 2px;color:#1a237e;letter-spacing:1px;}
  .header p{margin:2px 0;color:#555;font-size:12px;}
  .summary{display:flex;gap:20px;margin-bottom:14px;background:#e8eaf6;padding:10px 14px;border-radius:4px;font-size:12px;}
  .summary div{display:flex;flex-direction:column;align-items:center;}
  .summary .val{font-size:20px;font-weight:700;color:#1a237e;}
  .summary .lbl{color:#555;font-size:11px;}
  .section-title{font-weight:700;color:#1a237e;margin:16px 0 6px;font-size:13px;border-bottom:1px solid #9fa8da;padding-bottom:4px;}
  .section-sub{font-weight:700;color:#444;margin:12px 0 4px;font-size:12px;}
  table{width:100%;border-collapse:collapse;margin-bottom:8px;}
  th,td{border:1px solid #aaa;padding:5px 7px;text-align:left;vertical-align:top;}
  th{background:#e8eaf6;font-weight:700;color:#1a237e;}
  tr:nth-child(even) td{background:#f9f9f9;}
  @media print{body{margin:16px;}}
</style></head><body>
<div class="header">
  <h2>JBDE – Performance Report</h2>
  <p><strong>${popup.name}</strong> &nbsp;·&nbsp; ${roles}</p>
  <p>Period: ${popup.periodLabel || 'All time'} &nbsp;·&nbsp; Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
</div>
<div class="summary">
  <div><span class="val">${perfItems.length}</span><span class="lbl">Total</span></div>
  <div><span class="val">${longCount}</span><span class="lbl">Long Videos</span></div>
  <div><span class="val">${shortCount}</span><span class="lbl">Shorts</span></div>
  ${adCount ? `<div><span class="val">${adCount}</span><span class="lbl">Ads/Other</span></div>` : ''}
  ${deliveredItems.length ? `<div style="border-left:2px solid #7b1fa2;padding-left:12px;"><span class="val" style="color:#7b1fa2;">${deliveredItems.length}</span><span class="lbl">Content Delivered</span></div>` : ''}
</div>

${longItems.length ? `<div class="section-title">Long Videos (${longItems.length})</div>
<table>${perfTableHeader}<tbody>${makeRows(longItems)}</tbody></table>` : ''}

${shortItems.length ? `<div class="section-title">Shorts (${shortItems.length})</div>
<table>${perfTableHeader}<tbody>${makeRows(shortItems)}</tbody></table>` : ''}

${adItems.length ? `<div class="section-title">Ads / Other (${adItems.length})</div>
<table>${perfTableHeader}<tbody>${makeRows(adItems)}</tbody></table>` : ''}

${deliveredItems.length ? `<div class="section-title">Content Delivered (${deliveredItems.length})</div>
<table><thead><tr>
  <th>Content ID</th><th>Title</th><th>Type</th><th>Status</th><th>Content Ready Date</th>
</tr></thead><tbody>${deliveredItems.map(c => `<tr><td>${c.contentId||''}</td><td>${c.contentTitle||''}</td><td>${c.contentType||''}</td><td>${c.status||''}</td><td>${fmt(c.contentReadyAt||c.createdAt)}</td></tr>`).join('')}</tbody></table>` : ''}
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
    w.document.title = `Performance — ${popup.name}`;
    setTimeout(() => w.print(), 400);
  };

  useEffect(() => {
    console.log('useEffect triggered - Tab:', selectedTab, 'Week:', selectedWeek, 'Month:', selectedMonth);
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, selectedWeek, selectedMonth]);

  // Helper function to get target color based on actual vs target
  const getTargetColor = (channel, contentType, actual, isWeekly) => {
    const key = `${channel}|${contentType}`;
    const targetData = isWeekly ? analytics?.targets?.weekly : analytics?.targets?.monthly;
    const target = targetData?.[key] || 0;
    
    if (target === 0) return '#4facfe'; // No target set - default blue
    if (actual >= target) return '#4caf50'; // Met target - green
    if (actual >= target * 0.7) return '#ff9800'; // 70%+ - orange
    return '#f44336'; // Below 70% - red
  };

  // Helper function to show warning badge if target not met
  const showTargetBadge = (channel, contentType, actual, isWeekly) => {
    const key = `${channel}|${contentType}`;
    const targetData = isWeekly ? analytics?.targets?.weekly : analytics?.targets?.monthly;
    const target = targetData?.[key] || 0;
    
    return target > 0 && actual < target;
  };

  // Helper function to get tooltip text
  const getTargetTooltip = (channel, contentType, actual, isWeekly) => {
    const key = `${channel}|${contentType}`;
    const targetData = isWeekly ? analytics?.targets?.weekly : analytics?.targets?.monthly;
    const target = targetData?.[key] || 0;
    
    if (target === 0) return 'No target set';
    if (actual >= target) return `Target met! ${actual}/${target}`;
    return `Target: ${target}, Actual: ${actual}, Need: ${target - actual} more`;
  };

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function generateWeekOptions() {
    const options = [];
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - (i * 7));
      // Adjust to Monday
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      options.push({
        value: i,
        label: i === 0 ? 'This Week' : `${i} ${i === 1 ? 'Week' : 'Weeks'} Ago`,
        displayLabel: `${weekStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${weekEnd.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
      });
    }
    return options;
  }

  function generateMonthOptions() {
    const options = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label, isCurrent: i === 0 });
    }
    
    return options;
  }

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (selectedTab === 0) {
        // Weekly view - pass week offset
        params.weekOffset = selectedWeek;
        console.log('Fetching weekly data with offset:', selectedWeek);
      } else {
        // Monthly view - pass year and month
        const [year, month] = selectedMonth.split('-');
        params.year = year;
        params.month = month;
        console.log('Fetching monthly data for:', year, month);
      }
      
      console.log('API call params:', params);
      
      const [contentRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/content?limit=5`),
        axios.get(`${API_URL}/content/analytics/dashboard`, { params })
      ]);
      
      console.log('Analytics response:', analyticsRes.data.data);
      
      setRecentContent(contentRes.data.data.content);
      setAnalytics(analyticsRes.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isWeekly = selectedTab === 0;
  const releasedCount = isWeekly ? analytics?.overview?.weeklyReleased : analytics?.overview?.monthlyReleased;
  const contentTypes = isWeekly ? analytics?.contentTypes?.weekly : analytics?.contentTypes?.monthly;
  const channelBreakdown = isWeekly ? analytics?.channelBreakdown?.weekly : analytics?.channelBreakdown?.monthly;
  const directors = isWeekly ? analytics?.directors?.weekly : analytics?.directors?.monthly;
  const editors = isWeekly ? analytics?.editors?.weekly : analytics?.editors?.monthly;
  const cinematographers = isWeekly ? analytics?.cinematographers?.weekly : analytics?.cinematographers?.monthly;
  const actors = isWeekly ? analytics?.actors?.weekly : analytics?.actors?.monthly;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          <DashboardIcon sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }} /> Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/content/new')}
          size="small"
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Content</Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
        </Button>
      </Box>

      {/* Time Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Tabs 
              value={selectedTab} 
              onChange={(e, newValue) => {
                setSelectedTab(newValue);
                if (newValue === 0) setSelectedWeek(0);
                if (newValue === 1) setSelectedMonth(getCurrentMonth());
              }}
              sx={{ borderBottom: 1, borderColor: 'divider', flexGrow: 1 }}
            >
              <Tab label="📅 Weekly" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }} />
              <Tab label="📆 Monthly" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }} />
            </Tabs>
            
            {selectedTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Select Week</InputLabel>
                <Select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  label="Select Week"
                >
                  {generateWeekOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} ({option.displayLabel})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {selectedTab === 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Select Month"
                >
                  {generateMonthOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} {option.isCurrent && '(Current)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Content Overview - All 4 cards in one row */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
              <VideoIcon sx={{ fontSize: 40, opacity: 0.9, mb: 0.5 }} />
              <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                {releasedCount || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' }, display: 'block', mt: 0.5 }}>
                Content Released {isWeekly 
                  ? (selectedWeek === 0 ? 'This Week' : `Week ${selectedWeek} Ago`)
                  : generateMonthOptions().find(m => m.value === selectedMonth)?.label
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
              <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mt: 1 }}>
                {contentTypes?.['Long Video'] || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' }, display: 'block', mt: 1 }}>
                📹 Long Videos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
              <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mt: 1 }}>
                {contentTypes?.['Shorts'] || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' }, display: 'block', mt: 1 }}>
                🎬 Shorts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
              <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mt: 1 }}>
                {contentTypes?.['Ad'] || contentTypes?.['Ad Content'] || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' }, display: 'block', mt: 1 }}>
                📢 Ads
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Channel-wise Breakdown */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1.5} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
                📺 Channel-wise Breakdown
              </Typography>
              <TableContainer>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 } } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Channel</strong></TableCell>
                      <TableCell align="center">
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>LV</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Long</strong></Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>Sh</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Shorts</strong></Box>
                      </TableCell>
                      <TableCell align="center"><strong>Ads</strong></TableCell>
                      <TableCell align="center"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channelBreakdown && Object.keys(channelBreakdown).length > 0 ? (
                      Object.entries(channelBreakdown)
                        .sort((a, b) => b[1].total - a[1].total)
                        .map(([channel, data]) => (
                          <TableRow key={channel} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                {channel}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={getTargetTooltip(channel, 'Long Video', data['Long Video'] || 0, isWeekly)} arrow>
                                <Chip 
                                  label={data['Long Video'] || 0} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: getTargetColor(channel, 'Long Video', data['Long Video'] || 0, isWeekly), 
                                    color: 'white', 
                                    minWidth: { xs: 24, md: 32 }, 
                                    height: { xs: 20, md: 24 }, 
                                    fontSize: { xs: '0.65rem', md: '0.8125rem' } 
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={getTargetTooltip(channel, 'Shorts', data['Shorts'] || 0, isWeekly)} arrow>
                                <Chip 
                                  label={data['Shorts'] || 0} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: getTargetColor(channel, 'Shorts', data['Shorts'] || 0, isWeekly), 
                                    color: 'white', 
                                    minWidth: { xs: 24, md: 32 }, 
                                    height: { xs: 20, md: 24 }, 
                                    fontSize: { xs: '0.65rem', md: '0.8125rem' } 
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={getTargetTooltip(channel, 'Ad', data['Ad'] || data['Ad Content'] || 0, isWeekly)} arrow>
                                <Chip 
                                  label={data['Ad'] || data['Ad Content'] || 0} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: getTargetColor(channel, 'Ad', data['Ad'] || data['Ad Content'] || 0, isWeekly), 
                                    color: 'white', 
                                    minWidth: { xs: 24, md: 32 }, 
                                    height: { xs: 20, md: 24 }, 
                                    fontSize: { xs: '0.65rem', md: '0.8125rem' } 
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={data.total || 0} 
                                color="primary" 
                                size="small"
                                sx={{ fontWeight: 700, minWidth: { xs: 24, md: 32 }, height: { xs: 20, md: 24 }, fontSize: { xs: '0.65rem', md: '0.8125rem' } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Performance Statistics */}
      <Grid container spacing={2} mb={2}>
        {/* Cinematographers */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                🎥 Cinematographers Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1 }}><strong>Cinematographer</strong></TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>LV</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Long</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>Sh</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Shorts</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 1 }}><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cinematographers && cinematographers.length > 0 ? (
                      cinematographers.map((item, index) => (
                        <TableRow key={index} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openPersonWork(item.name, item.id, 'cinematographer')}>
                          <TableCell sx={{ py: 0.5, px: 1, color: 'primary.main', fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.longVideos || 0} size="small" sx={{ bgcolor: '#4facfe', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.shorts || 0} size="small" sx={{ bgcolor: '#fa709a', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <Chip label={item.count} color="warning" size="small" sx={{ fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Editors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                ✂️ Editors Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1 }}><strong>Editor</strong></TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>LV</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Long</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>Sh</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Shorts</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 1 }}><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editors && editors.length > 0 ? (
                      editors.map((item, index) => (
                        <TableRow key={index} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openPersonWork(item.name, item.id, 'editor')}>
                          <TableCell sx={{ py: 0.5, px: 1, color: 'primary.main', fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.longVideos || 0} size="small" sx={{ bgcolor: '#4facfe', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.shorts || 0} size="small" sx={{ bgcolor: '#fa709a', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <Chip label={item.count} color="success" size="small" sx={{ fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Actors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                🎭 Actors Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1 }}><strong>Actor</strong></TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>LV</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Long</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>Sh</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Shorts</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 1 }}><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actors && actors.length > 0 ? (
                      actors.map((item, index) => (
                        <TableRow key={index} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openPersonWork(item.name, item.id, 'actor')}>
                          <TableCell sx={{ py: 0.5, px: 1, color: 'primary.main', fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.longVideos || 0} size="small" sx={{ bgcolor: '#4facfe', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.shorts || 0} size="small" sx={{ bgcolor: '#fa709a', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <Chip label={item.count} color="secondary" size="small" sx={{ fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Directors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1} display="flex" alignItems="center" gap={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                🎬 Directors Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1 }}><strong>Director</strong></TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>LV</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Long</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}><strong>Sh</strong></Box>
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}><strong>Shorts</strong></Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 1 }}><strong>Total</strong></TableCell>
                      <Tooltip title="Content submitted (status moved to Content Ready) in this period" placement="top">
                        <TableCell align="center" sx={{ py: 0.5, px: 1, color: '#7b1fa2', cursor: 'help' }}>
                          <strong>Content Delivered</strong>
                        </TableCell>
                      </Tooltip>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {directors && directors.length > 0 ? (
                      directors.map((item, index) => (
                        <TableRow key={index} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openPersonWork(item.name, item.id, 'director')}>
                          <TableCell sx={{ py: 0.5, px: 1, color: 'primary.main', fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.longVideos || 0} size="small" sx={{ bgcolor: '#4facfe', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Chip label={item.shorts || 0} size="small" sx={{ bgcolor: '#fa709a', color: 'white', minWidth: 32 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <Chip label={item.count} color="secondary" size="small" sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            {item.newContent > 0
                              ? <Chip label={item.newContent} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 32, fontWeight: 700 }} />
                              : <Typography variant="caption" color="text.disabled">—</Typography>
                            }
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Release Compliance Widget - Full Width at Bottom */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <ReleaseComplianceWidget 
            selectedTab={selectedTab}
            selectedWeek={selectedWeek}
            selectedMonth={selectedMonth}
          />
        </Grid>
      </Grid>

      {/* Recent Content - Temporarily Hidden */}
      <Card sx={{ display: 'none' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            📹 Recent Content
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Channel</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Director</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentContent.length > 0 ? (
                  recentContent.map((content) => (
                    <TableRow 
                      key={content.id} 
                      hover 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/content/edit/${content.id}`)}
                    >
                      <TableCell>{content.contentTitle}</TableCell>
                      <TableCell>
                        <Chip label={content.channel} size="small" />
                      </TableCell>
                      <TableCell>{content.contentType}</TableCell>
                      <TableCell>
                        <Chip 
                          label={content.status} 
                          color={content.status === 'Released' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{content.director}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No content yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} textAlign="center">
            <Button variant="outlined" onClick={() => navigate('/content')}>
              View All Content
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Person Work Popup */}
      <Dialog open={!!personPopup} onClose={() => setPersonPopup(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1a237e', color: '#fff', py: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{personPopup?.name}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Consolidated Performance · {personPopup?.periodLabel} · {personPopup?.items?.length || 0} item(s)
              {personPopup?.items?.length > 0 && (() => {
                const lv = personPopup.items.filter(c => c.contentType === 'Long Video').length;
                const sh = personPopup.items.filter(c => c.contentType === 'Shorts').length;
                return <span style={{ marginLeft: 8, opacity: 0.85 }}>· LV: {lv} · Shorts: {sh}</span>;
              })()}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" variant="outlined" startIcon={<PdfIcon />}
              onClick={() => exportPersonWorkPDF(personPopup)}
              sx={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Export PDF
            </Button>
            <IconButton size="small" onClick={() => setPersonPopup(null)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {personPopup?.loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : personPopup?.items?.length === 0 ? (
            <Box p={4} textAlign="center"><Typography color="text.secondary">No content found for the selected period.</Typography></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Content ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Role(s)</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Content Ready</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Shoot Completed</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Editing Completed</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Release Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#5b21b6' }}>Edit Hrs</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0f766e' }}>Dir Hrs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {personPopup?.items?.map((c, i) => {
                    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    return (
                    <TableRow key={c.id} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#f9f9f9', '&:hover': { bgcolor: '#e8eaf630' } }}>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 500 }}>{c.contentId}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.contentTitle}</TableCell>
                      <TableCell>
                        <Chip label={c.contentType} size="small"
                          sx={{ bgcolor: c.contentType === 'Long Video' ? '#1565C0' : c.contentType === 'Shorts' ? '#E65100' : '#2E7D32', color: '#fff', fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {(c.roles || []).map(r => (
                          <Chip key={r} label={r} size="small" variant="outlined" sx={{ mr: 0.25, fontSize: '0.65rem' }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip label={c.status} size="small" variant="outlined"
                          color={c.status === 'Released' ? 'success' : c.status === 'Editing Completed' ? 'info' : 'default'} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmt(c.contentReadyAt || c.createdAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmt(c.shootCompletedAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmt(c.editingCompletedAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmt(c.releasedAt)}</TableCell>
                      <TableCell>
                        {c.totalEditingEffort != null
                          ? <Chip label={`${c.totalEditingEffort} hrs`} size="small" sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 600, fontSize: '0.7rem' }} />
                          : <span style={{ color: '#aaa' }}>—</span>}
                      </TableCell>
                      <TableCell>
                        {c.totalDirectingEffort != null
                          ? <Chip label={`${c.totalDirectingEffort} hrs`} size="small" sx={{ bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 600, fontSize: '0.7rem' }} />
                          : <span style={{ color: '#aaa' }}>—</span>}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
}

export default Dashboard;
